import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { unmaskPrimaryContact } from '~/api/sellerContacts';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button } from '~/components/ui';
import { sellerLabel } from '~/utils';
import type { Currency } from '~/graphql/graphql';
import { moneyOrDash } from './quoteDisplay';
import type { Quote } from './splitAwardGridTypes';
import { QuoteContactsButton } from './QuoteContactsButton';
import { SellerContacts } from './SellerContacts';

type OrderedQuote = Pick<
  Quote,
  | 'id'
  | 'status'
  | 'requestForQuote'
  | 'sellingOrganization'
  | 'primaryContact'
  | 'recipients'
  | 'lockedRecipientIds'
>;

export type SelectedQuoteLine = {
  quoteItemId: string;
  quote: OrderedQuote;
  requestForQuoteId: string;
  platformProductLengthId: string;
  productName: string;
  lengthDisplayValue: string;
  quantityInBundles: number;
  pickupPrice: { amount: number; currency: Currency } | null;
  deliveryPrice: { amount: number; currency: Currency } | null;
};

type SellerGroup = {
  quote: OrderedQuote;
  lines: SelectedQuoteLine[];
};

// One cart is created per quote, so a seller that quoted on two of the buy list's requests
// is two carts rather than one.
function groupLinesByQuote(lines: SelectedQuoteLine[]): SellerGroup[] {
  const groupsByQuoteId = new Map<string, SellerGroup>();
  for (const line of lines) {
    const group = groupsByQuoteId.get(line.quote.id) ?? {
      quote: line.quote,
      lines: [],
    };
    group.lines.push(line);
    groupsByQuoteId.set(line.quote.id, group);
  }
  return [...groupsByQuoteId.values()];
}

function PrimaryContactCard({ quote }: Readonly<{ quote: OrderedQuote }>) {
  const primaryContactId = unmaskPrimaryContact(quote.primaryContact)?.id;
  return (
    <div className="mb-2">
      <SellerContacts
        sellerName={sellerLabel(quote.sellingOrganization)}
        recipients={quote.recipients.filter(
          (recipient) => recipient.id === primaryContactId
        )}
        primaryContactId={primaryContactId}
      />
    </div>
  );
}

type CreateOrdersFromQuotesModalProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  selectedLines: SelectedQuoteLine[];
  zIndex?: number;
}>;

export function CreateOrdersFromQuotesModal({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  selectedLines,
  zIndex,
}: CreateOrdersFromQuotesModalProps) {
  const { t } = useTranslation('rfq-view');
  const sellerGroups = useMemo(
    () => groupLinesByQuote(selectedLines),
    [selectedLines]
  );

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} zIndex={zIndex}>
      <div
        className="bg-neut-bg-alt rounded-lg w-full max-w-[45rem]"
        data-testid="create-orders-from-quotes-modal"
      >
        <ModalHeader
          title={t('rfq_view.create_orders_modal_title', {
            count: sellerGroups.length,
          })}
          onClose={handleClose}
          closeDisabled={isSubmitting}
        />
        <div className="p-6 flex flex-col gap-8 max-h-[70vh] overflow-y-auto">
          {sellerGroups.map((group) => (
            <div
              key={group.quote.id}
              className="rounded-lg border border-neut-border p-3"
              data-testid={`seller-group-${group.quote.id}`}
            >
              <h3 className="text-sm font-semibold text-neut-prim mb-2">
                <QuoteContactsButton quote={group.quote} />
              </h3>
              <PrimaryContactCard quote={group.quote} />
              <ul className="flex flex-col gap-1 mb-2">
                {group.lines.map((line) => (
                  <li
                    key={line.quoteItemId}
                    className="flex items-center justify-between gap-8 text-sm text-neut-sec"
                  >
                    <span className="flex min-w-0 flex-wrap items-center gap-4">
                      <span className="truncate">{line.productName}</span>
                      <span>{line.lengthDisplayValue}</span>
                      <span>
                        {t('rfq_view.quantity_bundles_label', {
                          count: line.quantityInBundles,
                        })}
                      </span>
                    </span>
                    <span className="flex shrink-0 gap-8 text-right">
                      <span data-testid={`pickup-price-${line.quoteItemId}`}>
                        {t('rfq_view.create_orders_modal_pickup_label')}{' '}
                        <span className="font-medium text-neut-prim">
                          {moneyOrDash(line.pickupPrice)}
                        </span>
                      </span>
                      <span data-testid={`delivery-price-${line.quoteItemId}`}>
                        {t('rfq_view.create_orders_modal_delivery_label')}{' '}
                        <span className="font-medium text-neut-prim">
                          {moneyOrDash(line.deliveryPrice)}
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between gap-8 text-sm font-medium text-neut-prim border-t border-neut-border pt-2">
                <span>
                  {t('rfq_view.create_orders_modal_line_count_label')}
                </span>
                <span data-testid={`line-count-${group.quote.id}`}>
                  {group.lines.length}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="p-6 pt-0 flex justify-end gap-2">
          <Button
            variant="secondary"
            width="auto"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('rfq_view.create_orders_modal_cancel_button')}
          </Button>
          <Button
            variant="primary"
            width="auto"
            onClick={onConfirm}
            loading={isSubmitting}
            disabled={isSubmitting || selectedLines.length === 0}
            data-testid="submit-orders-button"
          >
            {t('rfq_view.create_orders_modal_submit_button')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
